import { useEffect, useState, useCallback } from 'react';
import { Plus, Pencil, Trash2, Loader2, X, Save, GripVertical, Check, ChevronDown, ChevronRight, Info } from 'lucide-react';
import { useToast } from '../ui/Toast';
import { lohnBuchungsbelegApi } from '../../services/lohnBuchungsbelegApi';
import type { TemplateLineDto, CostCenterDto, AccountVatDto, MatchMode } from '../../types/lohnBuchungsbeleg';

/** A template-line draft prefilled from an unmatched Lohnart (Feature 2). */
export type PrefillLine = Omit<TemplateLineDto, 'id'>;

interface Props {
    canEdit: boolean;
    /** When set (from an unmatched Lohnart), open the line editor prefilled with these values. */
    prefillLine?: PrefillLine | null;
    /** Called after a prefilled line is saved, so the caller can recompute the voucher. */
    onPrefillSaved?: () => void;
}

const MATCH_MODES: MatchMode[] = ['SollHabenLohnart', 'SollLohnart', 'SollHaben', 'SollHabenBeschreibung', 'None'];
const MATCH_LABEL: Record<MatchMode, string> = {
    SollHabenLohnart: 'Soll + Haben + Lohnart-Nr.',
    SollLohnart: 'Soll + Lohnart-Nr.',
    SollHaben: 'Soll + Haben (Kontopaar, ohne Lohnart)',
    SollHabenBeschreibung: 'Soll + Haben + Beschreibung (roher Text)',
    None: 'Keine Berechnung (Platzhalter)',
};
const MATCH_HINT: Record<MatchMode, string> = {
    SollHabenLohnart: 'Standard: erfasst Rohdaten-Zeilen mit gleichem Soll- und Haben-Konto und gleicher Lohnart-Nummer.',
    SollLohnart: 'Erfasst nach Soll-Konto und Lohnart-Nummer, ohne aufs Haben-Konto zu achten.',
    SollHaben: 'Erfasst nur nach dem Konto-Paar Soll/Haben, unabhängig von der Lohnart (z.B. Sozialabzüge).',
    SollHabenBeschreibung: 'Wie Standard, aber vergleicht den rohen Lohnart-Text 1:1 statt der Nummer.',
    None: 'Diese Zeile berechnet nichts — reiner Platzhalter, damit die Reihenfolge/Struktur erhalten bleibt.',
};

type LineDraft = Omit<TemplateLineDto, 'id'> & { id?: number };

export function VorlageTab({ canEdit, prefillLine, onPrefillSaved }: Props) {
    const { toast } = useToast();
    const [lines, setLines] = useState<TemplateLineDto[]>([]);
    const [costCenters, setCostCenters] = useState<CostCenterDto[]>([]);
    const [vatAccounts, setVatAccounts] = useState<AccountVatDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState<LineDraft | null>(null);
    // True while the currently-open editor was opened from an unmatched-Lohnart prefill.
    const [fromPrefill, setFromPrefill] = useState(false);
    // Drag-and-drop reorder state.
    const [dragId, setDragId] = useState<number | null>(null);
    const [dragOverId, setDragOverId] = useState<number | null>(null);
    const [savingOrder, setSavingOrder] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await lohnBuchungsbelegApi.getTemplate();
            setLines(res.lines);
            setCostCenters(res.costCenters);
            setVatAccounts(res.vatAccounts ?? []);
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Vorlage konnte nicht geladen werden', 'error');
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => { void load(); }, [load]);

    // Feature 2: when arriving with a prefill (from an unmatched Lohnart), open the editor
    // with those values, appending it after the last line for a sensible sort order.
    useEffect(() => {
        if (!prefillLine) return;
        const nextSort = (lines[lines.length - 1]?.sortOrder ?? 0) + 10;
        setEditing({ ...prefillLine, sortOrder: prefillLine.sortOrder || nextSort });
        setFromPrefill(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefillLine]);

    const closeEditor = () => { setEditing(null); setFromPrefill(false); };

    const saveLine = async (draft: LineDraft) => {
        const body = {
            sortOrder: draft.sortOrder, sollAccount: draft.sollAccount, habenAccount: draft.habenAccount,
            beschreibung: draft.beschreibung, matchMode: draft.matchMode, sign: draft.sign,
            splitByCostCenter: draft.splitByCostCenter, targetCostCenter: draft.targetCostCenter,
            textLabel: draft.textLabel, includeInChecksum: draft.includeInChecksum,
        };
        try {
            if (draft.id) await lohnBuchungsbelegApi.updateLine(draft.id, body);
            else await lohnBuchungsbelegApi.createLine(body);
            toast('Zeile gespeichert', 'success');
            const wasPrefill = fromPrefill;
            closeEditor();
            await load();
            if (wasPrefill) onPrefillSaved?.();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Speichern fehlgeschlagen', 'error');
        }
    };

    const deleteLine = async (id: number) => {
        if (!confirm('Diese Buchungszeile aus der Vorlage entfernen?')) return;
        try {
            await lohnBuchungsbelegApi.deleteLine(id);
            await load();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Löschen fehlgeschlagen', 'error');
        }
    };

    const addLine = () => setEditing({
        sortOrder: (lines[lines.length - 1]?.sortOrder ?? 0) + 10,
        sollAccount: '', habenAccount: '', beschreibung: '', matchMode: 'SollHabenLohnart',
        sign: 1, splitByCostCenter: true, targetCostCenter: null, textLabel: '', includeInChecksum: true,
    });

    // --- Drag & drop reordering ---
    const onDrop = async (targetId: number) => {
        const from = lines.findIndex(l => l.id === dragId);
        const to = lines.findIndex(l => l.id === targetId);
        setDragId(null); setDragOverId(null);
        if (from < 0 || to < 0 || from === to) return;
        const next = [...lines];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        setLines(next); // optimistic
        setSavingOrder(true);
        try {
            await lohnBuchungsbelegApi.reorderLines(next.map(l => l.id));
            await load();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Reihenfolge konnte nicht gespeichert werden', 'error');
            await load(); // revert to server state
        } finally { setSavingOrder(false); }
    };

    if (loading) return <div className="py-16 text-center text-slate-300"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;

    return (
        <div className="space-y-8">
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                Die Vorlage definiert, wie der Buchungsbeleg aus den Rohdaten berechnet wird. Änderungen wirken
                sich auf alle noch nicht finalisierten Belege aus. Finalisierte Monate behalten ihren Stand.
            </div>

            {/* Cost centers — editable table with code + label */}
            <CostCenterSection costCenters={costCenters} canEdit={canEdit} onChanged={load} />

            {/* Template lines */}
            <section>
                <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-slate-700">Buchungszeilen ({lines.length})</h3>
                        {savingOrder && <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Loader2 className="h-3 w-3 animate-spin" /> Reihenfolge speichern…</span>}
                    </div>
                    {canEdit && (
                        <button onClick={addLine} className="inline-flex items-center gap-1 rounded bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700">
                            <Plus className="h-3.5 w-3.5" /> Zeile hinzufügen
                        </button>
                    )}
                </div>
                {canEdit && <p className="mb-2 text-xs text-slate-400">Zeilen per <GripVertical className="inline h-3 w-3" />-Griff ziehen, um die Reihenfolge zu ändern. Details (Zuordnungs-Methode, Vorzeichen) im Bearbeiten-Dialog.</p>}
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="min-w-full text-xs">
                        <thead className="bg-slate-100 text-slate-600">
                            <tr>
                                {canEdit && <th className="w-6 px-1 py-1.5" />}
                                <th className="px-2 py-1.5 text-left">Soll</th>
                                <th className="px-2 py-1.5 text-left">Haben</th>
                                <th className="px-2 py-1.5 text-left">Beschreibung</th>
                                <th className="px-2 py-1.5 text-left">Kostenstelle</th>
                                <th className="px-2 py-1.5 text-left">Text</th>
                                {canEdit && <th className="px-2 py-1.5" />}
                            </tr>
                        </thead>
                        <tbody>
                            {lines.map(l => (
                                <tr
                                    key={l.id}
                                    draggable={canEdit}
                                    onDragStart={() => setDragId(l.id)}
                                    onDragOver={e => { if (canEdit && dragId !== null) { e.preventDefault(); setDragOverId(l.id); } }}
                                    onDrop={e => { e.preventDefault(); void onDrop(l.id); }}
                                    onDragEnd={() => { setDragId(null); setDragOverId(null); }}
                                    onClick={canEdit ? () => setEditing({ ...l }) : undefined}
                                    className={[
                                        'border-t border-slate-100',
                                        dragOverId === l.id && dragId !== l.id ? 'bg-amber-50 ring-1 ring-inset ring-amber-300' : 'hover:bg-slate-50',
                                        dragId === l.id ? 'opacity-40' : '',
                                        canEdit ? 'cursor-pointer' : '',
                                    ].join(' ')}
                                >
                                    {canEdit && (
                                        <td className="px-1 py-1 text-center text-slate-300" onClick={e => e.stopPropagation()}>
                                            <GripVertical className="mx-auto h-3.5 w-3.5 cursor-grab active:cursor-grabbing" />
                                        </td>
                                    )}
                                    <td className="px-2 py-1 text-left font-mono text-slate-600">{l.sollAccount ?? ''}</td>
                                    <td className="px-2 py-1 text-left font-mono text-slate-600">{l.habenAccount ?? ''}</td>
                                    <td className="px-2 py-1 text-slate-700">
                                        {l.beschreibung}
                                        {l.matchMode === 'None' && <span className="ml-1 rounded bg-slate-100 px-1 text-[10px] text-slate-400">Platzhalter</span>}
                                        {l.sign < 0 && <span className="ml-1 rounded bg-rose-50 px-1 text-[10px] text-rose-500" title="Betrag wird abgezogen">− Abzug</span>}
                                    </td>
                                    <td className="px-2 py-1 text-slate-500">{l.splitByCostCenter ? 'alle (Split)' : `→ ${l.targetCostCenter ?? '(erste)'}`}</td>
                                    <td className="px-2 py-1 text-slate-400">{l.textLabel ?? ''}</td>
                                    {canEdit && (
                                        <td className="px-2 py-1 text-right" onClick={e => e.stopPropagation()}>
                                            <div className="flex justify-end gap-1">
                                                <button onClick={() => setEditing({ ...l })} title="Bearbeiten" className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-3.5 w-3.5" /></button>
                                                <button onClick={() => deleteLine(l.id)} title="Löschen" className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Per-account MWST posting groups (fill the BC-Export MWST columns) */}
            <AccountVatSection vatAccounts={vatAccounts} canEdit={canEdit} onChanged={load} />

            {editing && (
                <LineEditModal draft={editing} costCenters={costCenters} onSave={saveLine} onClose={closeEditor} fromPrefill={fromPrefill} />
            )}
        </div>
    );
}

/**
 * Per-account MWST posting groups for the BC export. In BC (Dynamics) the
 * MWST-Geschäfts-/Produktbuchungsgruppe are properties of the G/L account, so they are
 * maintained per Konto here and looked up by account when the BC clipboard is built —
 * accounts without an entry simply get empty MWST columns (the normal case).
 */
function AccountVatSection({ vatAccounts, canEdit, onChanged }: { vatAccounts: AccountVatDto[]; canEdit: boolean; onChanged: () => void }) {
    const { toast } = useToast();
    const [adding, setAdding] = useState(false);
    const [newAccount, setNewAccount] = useState('');
    const [newGbg, setNewGbg] = useState('');
    const [newPbg, setNewPbg] = useState('');
    const [editId, setEditId] = useState<number | null>(null);
    const [editGbg, setEditGbg] = useState('');
    const [editPbg, setEditPbg] = useState('');
    const [busy, setBusy] = useState(false);

    const startEdit = (v: AccountVatDto) => { setEditId(v.id); setEditGbg(v.mwstGeschaeft ?? ''); setEditPbg(v.mwstProdukt ?? ''); };

    const add = async () => {
        const account = newAccount.trim();
        if (!account) return;
        setBusy(true);
        try {
            await lohnBuchungsbelegApi.createVatAccount({
                account, mwstGeschaeft: newGbg.trim() || null, mwstProdukt: newPbg.trim() || null,
            });
            setNewAccount(''); setNewGbg(''); setNewPbg(''); setAdding(false);
            onChanged();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Hinzufügen fehlgeschlagen', 'error');
        } finally { setBusy(false); }
    };

    const save = async (v: AccountVatDto) => {
        setBusy(true);
        try {
            await lohnBuchungsbelegApi.updateVatAccount(v.id, {
                mwstGeschaeft: editGbg.trim() || null, mwstProdukt: editPbg.trim() || null,
            });
            setEditId(null);
            onChanged();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Speichern fehlgeschlagen', 'error');
        } finally { setBusy(false); }
    };

    const del = async (v: AccountVatDto) => {
        if (!confirm(`MWST-Angaben für Konto ${v.account} entfernen? Sie können später wieder hinzugefügt werden.`)) return;
        setBusy(true);
        try { await lohnBuchungsbelegApi.deleteVatAccount(v.id); onChanged(); }
        catch (e) { toast(e instanceof Error ? e.message : 'Löschen fehlgeschlagen', 'error'); }
        finally { setBusy(false); }
    };

    return (
        <section>
            <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-700">MWST pro Konto — BC-Export ({vatAccounts.length})</h3>
                {canEdit && !adding && (
                    <button onClick={() => setAdding(true)} className="inline-flex items-center gap-1 rounded bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700">
                        <Plus className="h-3.5 w-3.5" /> Konto
                    </button>
                )}
            </div>
            <p className="mb-2 text-xs text-slate-400">
                Befüllt beim BC-Export die Spalten «MWST-Geschäftsbuchungsgruppe» und «MWST-Produktbuchungsgruppe» für
                Buchungssätze mit diesem Konto (z.B. 58212 Privatanteil Fahrzeug: INLAND / NORMAL-24). Konten ohne
                Eintrag bleiben leer. Damit die Werte in die Zwischenablage kommen, müssen die beiden Spalten im
                Tab BC-Export aktiviert und an der passenden Position sein.
            </p>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full text-xs">
                    <thead className="bg-slate-100 text-slate-600">
                        <tr>
                            <th className="px-3 py-1.5 text-left">Konto</th>
                            <th className="px-3 py-1.5 text-left">MWST-Geschäftsbuchungsgruppe</th>
                            <th className="px-3 py-1.5 text-left">MWST-Produktbuchungsgruppe</th>
                            {canEdit && <th className="px-3 py-1.5" />}
                        </tr>
                    </thead>
                    <tbody>
                        {vatAccounts.length === 0 && !adding && (
                            <tr><td colSpan={canEdit ? 4 : 3} className="px-3 py-4 text-center text-slate-300">Noch keine Konten erfasst.</td></tr>
                        )}
                        {vatAccounts.map(v => (
                            <tr
                                key={v.id}
                                onClick={canEdit && editId !== v.id ? () => startEdit(v) : undefined}
                                className={`border-t border-slate-100 hover:bg-slate-50 ${canEdit && editId !== v.id ? 'cursor-pointer' : ''}`}
                            >
                                <td className="px-3 py-1.5 font-mono font-medium text-slate-700">{v.account}</td>
                                {editId === v.id ? (
                                    <>
                                        <td className="px-3 py-1.5" onClick={e => e.stopPropagation()}>
                                            <input
                                                value={editGbg}
                                                onChange={e => setEditGbg(e.target.value)}
                                                onKeyDown={e => { if (e.key === 'Enter') void save(v); if (e.key === 'Escape') setEditId(null); }}
                                                autoFocus
                                                placeholder="z.B. INLAND"
                                                className="w-40 rounded border border-slate-300 px-2 py-1 font-mono text-xs focus:border-amber-400 focus:outline-none"
                                            />
                                        </td>
                                        <td className="px-3 py-1.5" onClick={e => e.stopPropagation()}>
                                            <input
                                                value={editPbg}
                                                onChange={e => setEditPbg(e.target.value)}
                                                onKeyDown={e => { if (e.key === 'Enter') void save(v); if (e.key === 'Escape') setEditId(null); }}
                                                placeholder="z.B. NORMAL-24"
                                                className="w-40 rounded border border-slate-300 px-2 py-1 font-mono text-xs focus:border-amber-400 focus:outline-none"
                                            />
                                        </td>
                                    </>
                                ) : (
                                    <>
                                        <td className="px-3 py-1.5 font-mono text-slate-600">{v.mwstGeschaeft ?? <span className="text-slate-300">(leer)</span>}</td>
                                        <td className="px-3 py-1.5 font-mono text-slate-600">{v.mwstProdukt ?? <span className="text-slate-300">(leer)</span>}</td>
                                    </>
                                )}
                                {canEdit && (
                                    <td className="px-3 py-1.5 text-right" onClick={e => e.stopPropagation()}>
                                        <div className="flex justify-end gap-1">
                                            {editId === v.id ? (
                                                <>
                                                    <button onClick={() => save(v)} disabled={busy} title="Speichern" className="rounded p-1 text-emerald-500 hover:bg-emerald-50"><Check className="h-3.5 w-3.5" /></button>
                                                    <button onClick={() => setEditId(null)} title="Abbrechen" className="rounded p-1 text-slate-400 hover:bg-slate-100"><X className="h-3.5 w-3.5" /></button>
                                                </>
                                            ) : (
                                                <>
                                                    <button onClick={() => startEdit(v)} title="Bearbeiten" className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-3.5 w-3.5" /></button>
                                                    <button onClick={() => del(v)} disabled={busy} title="Entfernen" className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))}
                        {adding && canEdit && (
                            <tr className="border-t border-amber-200 bg-amber-50/50">
                                <td className="px-3 py-1.5">
                                    <input value={newAccount} onChange={e => setNewAccount(e.target.value)} placeholder="Konto" autoFocus className="w-24 rounded border border-slate-300 px-2 py-1 font-mono text-xs focus:border-amber-400 focus:outline-none" />
                                </td>
                                <td className="px-3 py-1.5">
                                    <input value={newGbg} onChange={e => setNewGbg(e.target.value)} placeholder="z.B. INLAND" className="w-40 rounded border border-slate-300 px-2 py-1 font-mono text-xs focus:border-amber-400 focus:outline-none" />
                                </td>
                                <td className="px-3 py-1.5">
                                    <input value={newPbg} onChange={e => setNewPbg(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') void add(); }} placeholder="z.B. NORMAL-24" className="w-40 rounded border border-slate-300 px-2 py-1 font-mono text-xs focus:border-amber-400 focus:outline-none" />
                                </td>
                                <td className="px-3 py-1.5 text-right">
                                    <div className="flex justify-end gap-1">
                                        <button onClick={add} disabled={busy || !newAccount.trim()} title="Hinzufügen" className="rounded p-1 text-emerald-500 hover:bg-emerald-50 disabled:opacity-40">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}</button>
                                        <button onClick={() => { setAdding(false); setNewAccount(''); setNewGbg(''); setNewPbg(''); }} title="Abbrechen" className="rounded p-1 text-slate-400 hover:bg-slate-100"><X className="h-3.5 w-3.5" /></button>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

/** Cost-center management: an editable, drag-reorderable table with code + Bezeichnung (label). */
function CostCenterSection({ costCenters, canEdit, onChanged }: { costCenters: CostCenterDto[]; canEdit: boolean; onChanged: () => void }) {
    const { toast } = useToast();
    const [adding, setAdding] = useState(false);
    const [newCode, setNewCode] = useState('');
    const [newLabel, setNewLabel] = useState('');
    const [editId, setEditId] = useState<number | null>(null);
    const [editLabel, setEditLabel] = useState('');
    const [busy, setBusy] = useState(false);
    // Local mirror so drag reorders render optimistically before the server round-trip.
    const [order, setOrder] = useState<CostCenterDto[]>(costCenters);
    const [dragId, setDragId] = useState<number | null>(null);
    const [dragOverId, setDragOverId] = useState<number | null>(null);
    const [savingOrder, setSavingOrder] = useState(false);
    useEffect(() => { setOrder(costCenters); }, [costCenters]);

    const onDrop = async (targetId: number) => {
        const from = order.findIndex(c => c.id === dragId);
        const to = order.findIndex(c => c.id === targetId);
        setDragId(null); setDragOverId(null);
        if (from < 0 || to < 0 || from === to) return;
        const next = [...order];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        setOrder(next); // optimistic
        setSavingOrder(true);
        try {
            await lohnBuchungsbelegApi.reorderCostCenters(next.map(c => c.id));
            onChanged();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Reihenfolge konnte nicht gespeichert werden', 'error');
            onChanged();
        } finally { setSavingOrder(false); }
    };

    const add = async () => {
        const code = newCode.trim();
        if (!code) return;
        setBusy(true);
        try {
            await lohnBuchungsbelegApi.createCostCenter({
                costCenterCode: code,
                columnLabel: newLabel.trim() || `KST ${code}`,
                sortOrder: (costCenters[costCenters.length - 1]?.sortOrder ?? 0) + 10,
            });
            setNewCode(''); setNewLabel(''); setAdding(false);
            onChanged();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Hinzufügen fehlgeschlagen', 'error');
        } finally { setBusy(false); }
    };

    const saveLabel = async (cc: CostCenterDto) => {
        setBusy(true);
        try {
            await lohnBuchungsbelegApi.updateCostCenter(cc.id, { columnLabel: editLabel.trim() || null, sortOrder: cc.sortOrder });
            setEditId(null);
            onChanged();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Speichern fehlgeschlagen', 'error');
        } finally { setBusy(false); }
    };

    const del = async (cc: CostCenterDto) => {
        if (!confirm(`Kostenstelle ${cc.code} entfernen? Sie kann später wieder hinzugefügt werden.`)) return;
        setBusy(true);
        try { await lohnBuchungsbelegApi.deleteCostCenter(cc.id); onChanged(); }
        catch (e) { toast(e instanceof Error ? e.message : 'Löschen fehlgeschlagen', 'error'); }
        finally { setBusy(false); }
    };

    return (
        <section>
            <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-slate-700">Kostenstellen ({costCenters.length} Spalten)</h3>
                    {savingOrder && <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Loader2 className="h-3 w-3 animate-spin" /> Reihenfolge speichern…</span>}
                </div>
                {canEdit && !adding && (
                    <button onClick={() => setAdding(true)} className="inline-flex items-center gap-1 rounded bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700">
                        <Plus className="h-3.5 w-3.5" /> Kostenstelle
                    </button>
                )}
            </div>
            {canEdit && <p className="mb-2 text-xs text-slate-400">Reihenfolge der Spalten per <GripVertical className="inline h-3 w-3" />-Griff ziehen.</p>}
            <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full text-xs">
                    <thead className="bg-slate-100 text-slate-600">
                        <tr>
                            {canEdit && <th className="w-6 px-1 py-1.5" />}
                            <th className="px-3 py-1.5 text-left">Kostenstelle</th>
                            <th className="px-3 py-1.5 text-left">Bezeichnung</th>
                            <th className="px-3 py-1.5 text-left">Reihenfolge</th>
                            {canEdit && <th className="px-3 py-1.5" />}
                        </tr>
                    </thead>
                    <tbody>
                        {order.map(cc => (
                            <tr
                                key={cc.id}
                                draggable={canEdit && editId !== cc.id}
                                onDragStart={() => setDragId(cc.id)}
                                onDragOver={e => { if (canEdit && dragId !== null) { e.preventDefault(); setDragOverId(cc.id); } }}
                                onDrop={e => { e.preventDefault(); void onDrop(cc.id); }}
                                onDragEnd={() => { setDragId(null); setDragOverId(null); }}
                                onClick={canEdit && editId !== cc.id ? () => { setEditId(cc.id); setEditLabel(cc.label ?? ''); } : undefined}
                                className={[
                                    'border-t border-slate-100',
                                    dragOverId === cc.id && dragId !== cc.id ? 'bg-amber-50 ring-1 ring-inset ring-amber-300' : 'hover:bg-slate-50',
                                    dragId === cc.id ? 'opacity-40' : '',
                                    canEdit && editId !== cc.id ? 'cursor-pointer' : '',
                                ].join(' ')}
                            >
                                {canEdit && (
                                    <td className="px-1 py-1.5 text-center text-slate-300" onClick={e => e.stopPropagation()}>
                                        <GripVertical className="mx-auto h-3.5 w-3.5 cursor-grab active:cursor-grabbing" />
                                    </td>
                                )}
                                <td className="px-3 py-1.5 font-mono font-medium text-slate-700">{cc.code}</td>
                                <td className="px-3 py-1.5 text-slate-600">
                                    {editId === cc.id ? (
                                        <input
                                            value={editLabel}
                                            onChange={e => setEditLabel(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') void saveLabel(cc); if (e.key === 'Escape') setEditId(null); }}
                                            onClick={e => e.stopPropagation()}
                                            autoFocus
                                            className="w-56 rounded border border-slate-300 px-2 py-1 text-xs focus:border-amber-400 focus:outline-none"
                                        />
                                    ) : (
                                        <span className={cc.label ? '' : 'text-slate-300'}>{cc.label || '(keine)'}</span>
                                    )}
                                </td>
                                <td className="px-3 py-1.5 text-slate-400">{cc.sortOrder}</td>
                                {canEdit && (
                                    <td className="px-3 py-1.5 text-right" onClick={e => e.stopPropagation()}>
                                        <div className="flex justify-end gap-1">
                                            {editId === cc.id ? (
                                                <>
                                                    <button onClick={() => saveLabel(cc)} disabled={busy} title="Speichern" className="rounded p-1 text-emerald-500 hover:bg-emerald-50"><Check className="h-3.5 w-3.5" /></button>
                                                    <button onClick={() => setEditId(null)} title="Abbrechen" className="rounded p-1 text-slate-400 hover:bg-slate-100"><X className="h-3.5 w-3.5" /></button>
                                                </>
                                            ) : (
                                                <>
                                                    <button onClick={() => { setEditId(cc.id); setEditLabel(cc.label ?? ''); }} title="Bezeichnung bearbeiten" className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-3.5 w-3.5" /></button>
                                                    <button onClick={() => del(cc)} disabled={busy} title="Entfernen" className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))}
                        {adding && canEdit && (
                            <tr className="border-t border-amber-200 bg-amber-50/50">
                                <td className="px-1 py-1.5" />
                                <td className="px-3 py-1.5">
                                    <input value={newCode} onChange={e => setNewCode(e.target.value)} placeholder="Code" autoFocus className="w-24 rounded border border-slate-300 px-2 py-1 font-mono text-xs focus:border-amber-400 focus:outline-none" />
                                </td>
                                <td className="px-3 py-1.5">
                                    <input value={newLabel} onChange={e => setNewLabel(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') void add(); }} placeholder="Bezeichnung (optional)" className="w-56 rounded border border-slate-300 px-2 py-1 text-xs focus:border-amber-400 focus:outline-none" />
                                </td>
                                <td />
                                <td className="px-3 py-1.5 text-right">
                                    <div className="flex justify-end gap-1">
                                        <button onClick={add} disabled={busy || !newCode.trim()} title="Hinzufügen" className="rounded p-1 text-emerald-500 hover:bg-emerald-50 disabled:opacity-40">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}</button>
                                        <button onClick={() => { setAdding(false); setNewCode(''); setNewLabel(''); }} title="Abbrechen" className="rounded p-1 text-slate-400 hover:bg-slate-100"><X className="h-3.5 w-3.5" /></button>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function LineEditModal({ draft, costCenters, onSave, onClose, fromPrefill }: { draft: LineDraft; costCenters: CostCenterDto[]; onSave: (d: LineDraft) => void; onClose: () => void; fromPrefill?: boolean }) {
    const [d, setD] = useState<LineDraft>(draft);
    const [saving, setSaving] = useState(false);
    const [advOpen, setAdvOpen] = useState(false);
    const set = (patch: Partial<LineDraft>) => setD(prev => ({ ...prev, ...patch }));
    const valid = d.beschreibung.trim().length > 0 && typeof d.sortOrder === 'number';

    const submit = async () => {
        if (!valid) return;
        setSaving(true);
        await onSave(d);
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-xl" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                    <h3 className="text-sm font-semibold text-slate-800">{d.id ? 'Buchungszeile bearbeiten' : fromPrefill ? 'Vorlage-Zeile aus Lohnart anlegen' : 'Neue Buchungszeile'}</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
                </div>
                {fromPrefill && (
                    <div className="mx-4 mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                        Vorausgefüllt aus einer nicht zugeordneten Lohnart. Prüfe Soll/Haben und speichere — der Beleg wird
                        danach neu berechnet und die Lohnart verschwindet aus der Diagnose.
                    </div>
                )}
                <div className="grid grid-cols-2 gap-3 px-4 py-4 text-sm">
                    <Field label="Beschreibung (Pflicht)" full><input value={d.beschreibung} onChange={e => set({ beschreibung: e.target.value })} className="fld" /></Field>
                    <Field label="Soll-Konto"><input value={d.sollAccount ?? ''} onChange={e => set({ sollAccount: e.target.value })} className="fld font-mono" /></Field>
                    <Field label="Haben-Konto"><input value={d.habenAccount ?? ''} onChange={e => set({ habenAccount: e.target.value })} className="fld font-mono" /></Field>
                    <Field label="Text-Label" full><input value={d.textLabel ?? ''} onChange={e => set({ textLabel: e.target.value })} className="fld" /></Field>
                </div>

                {/* Advanced — the technical fields that steer the computation. Collapsed by default. */}
                <div className="border-t border-slate-100 px-4">
                    <button onClick={() => setAdvOpen(o => !o)} className="flex w-full items-center gap-1.5 py-2.5 text-left text-xs font-medium text-slate-500 hover:text-slate-700">
                        {advOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        Erweiterte Einstellungen (Zuordnung, Vorzeichen, Kostenstellen)
                    </button>
                    {advOpen && (
                        <div className="grid grid-cols-2 gap-3 pb-4 text-sm">
                            <Field label="Zuordnungs-Methode" full hint="Legt fest, welche Rohdaten-Zeilen zu dieser Buchungszeile gezählt werden.">
                                <select value={d.matchMode} onChange={e => set({ matchMode: e.target.value as MatchMode })} className="fld">
                                    {MATCH_MODES.map(m => <option key={m} value={m}>{MATCH_LABEL[m]}</option>)}
                                </select>
                            </Field>
                            <div className="col-span-2 -mt-1 rounded bg-slate-50 px-2 py-1.5 text-[11px] text-slate-500">{MATCH_HINT[d.matchMode]}</div>
                            <Field label="Vorzeichen" hint="+ addiert den Betrag, − zieht ihn ab (z.B. Sozialabzüge, Nettolohn).">
                                <select value={d.sign} onChange={e => set({ sign: Number(e.target.value) })} className="fld">
                                    <option value={1}>+ addieren</option>
                                    <option value={-1}>− abziehen</option>
                                </select>
                            </Field>
                            <Field label="Kostenstellen" hint="Split verteilt den Betrag auf die KST aus den Rohdaten; sonst landet er in einer festen Ziel-KST.">
                                <label className="mt-1 inline-flex items-center gap-2 text-sm text-slate-600">
                                    <input type="checkbox" checked={d.splitByCostCenter} onChange={e => set({ splitByCostCenter: e.target.checked })} /> auf alle KST aufteilen (Split)
                                </label>
                            </Field>
                            {!d.splitByCostCenter && (
                                <Field label="Ziel-Kostenstelle">
                                    <select value={d.targetCostCenter ?? ''} onChange={e => set({ targetCostCenter: e.target.value || null })} className="fld">
                                        <option value="">(erste)</option>
                                        {costCenters.map(c => <option key={c.code} value={c.code}>{c.code}{c.label ? ` — ${c.label}` : ''}</option>)}
                                    </select>
                                </Field>
                            )}
                            <Field label="Kontroll-Summe" hint="Ob diese Zeile in die Checksumme eingeht (AG-Beiträge z.B. nicht).">
                                <label className="mt-1 inline-flex items-center gap-2 text-sm text-slate-600">
                                    <input type="checkbox" checked={d.includeInChecksum} onChange={e => set({ includeInChecksum: e.target.checked })} /> in Checksumme berücksichtigen
                                </label>
                            </Field>
                            <Field label="Sortierung"><input type="number" value={d.sortOrder} onChange={e => set({ sortOrder: Number(e.target.value) })} className="fld" /></Field>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-4 py-3">
                    <button onClick={onClose} className="rounded px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700">Abbrechen</button>
                    <button onClick={submit} disabled={!valid || saving} className="inline-flex items-center gap-1 rounded bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50">
                        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Speichern
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, full, hint, children }: { label: string; full?: boolean; hint?: string; children: React.ReactNode }) {
    return (
        <label className={`block ${full ? 'col-span-2' : ''}`}>
            <span className="flex items-center gap-1 text-xs font-medium text-slate-600">
                {label}
                {hint && <span title={hint}><Info className="h-3 w-3 text-slate-300" /></span>}
            </span>
            <div className="mt-1 [&_.fld]:w-full [&_.fld]:rounded [&_.fld]:border [&_.fld]:border-slate-300 [&_.fld]:px-2 [&_.fld]:py-1.5 [&_.fld]:text-sm [&_.fld]:focus:border-amber-400 [&_.fld]:focus:outline-none">
                {children}
            </div>
        </label>
    );
}
